import { EventDispatcher, Vector2, Vector3 } from 'three';
import { EVENT_CHANGED, EVENT_ROOM_ATTRIBUTES_CHANGED, EVENT_MOVED, EVENT_UPDATED, EVENT_CORNER_ATTRIBUTES_CHANGED,EVENT_ROOM_METADATA_CHANGED } from '../core/events.js';
import { Region } from '../core/utils.js';
import { WallTypes } from '../core/constants.js';
// import { / } from '../core/constants';
import { Utils } from '../core/utils.js';
import { HalfEdge } from './half_edge.js';

/**
 * A Room is the combination of a Floorplan with a floor plane.
 */
export class Room extends EventDispatcher {
    /**
     *  ordered CCW
     */
    constructor(floorplan, corners) {
        super();
        this._code = 'none';
        this._name = 'A New Area';
        this._factor = 1;
        this._roomType = 'non';
        this._roomBaseColor ='#AA336A';
        this.min = null;
        this.max = null;
        this.center = null;
        this.normal = null;

        this.__isDelete = false;
        this.area = 0.0;
        this.areaCenter = null;
        this._polygonPoints = [];
        this.__walls = [];

        this.floorplan = floorplan;
        this._corners = corners;
        this.interiorCorners = [];
        this.interiorCorners3D = [];
        this.floorRectangleSize = new Vector2();
        this.edgePointer = null;
        this.floorPlane = null;
        this.roofPlane = null;
        this.customTexture = false;
        this.floorChangeCallbacks = null;

        this.__cornerids = [];

        this.__destroyed = false;

        this.__isLocked = false;

        this.updateWalls();
        this.updateInteriorCorners();
       

        let cornerids = [];
        let i = 0;

        this.__roomUpdatedEvent = this._roomUpdated.bind(this);
        this.__wallsChangedEvent = this.__wallsChanged.bind(this);

        for (; i < this.corners.length; i++) {
            let c = this.corners[i];
            // console.log(c)
            c.attachRoom(this);
            cornerids.push(c.id);
            c.addEventListener(EVENT_MOVED, this.__roomUpdatedEvent);
            c.addEventListener(EVENT_CORNER_ATTRIBUTES_CHANGED, this.__roomUpdatedEvent);
        }

        for (i = 0; i < this.__walls.length; i++) {
            let wall = this.__walls[i];
            // console.log(wall);
            // cornerids.push(wall.id);
            wall.addRoom(this);
            wall.addEventListener(EVENT_UPDATED, this.__wallsChangedEvent);
        }
        this.__cornerids = cornerids;
        this._roomByCornersId = cornerids.join(',');
    }

    __wallsChanged(evt) {
        this.dispatchEvent({ type: EVENT_CHANGED, item: this });
        this.updateInteriorCorners();
    }

    _roomUpdated() {
        this.updateInteriorCorners();
        this.updateArea();
      
    }

    destroy() {
        let i = 0;
        for (; i < this.corners.length; i++) {
            let c = this.corners[i];
            c.removeEventListener(EVENT_MOVED, this.__roomUpdatedEvent);
        }
        for (i = 0; i < this.__walls.length; i++) {
            let wall = this.__walls[i];
            wall.removeEventListener(EVENT_UPDATED, this.__wallsChangedEvent);
        }
        this.__destroyed = true;
        this.dispatchEvent({ type: EVENT_CHANGED, item: this });
    }

    __getOrderedCorners(wall) {
        let i = this.corners.indexOf(wall.start);
        let j = this.corners.indexOf(wall.end);
        if (i === -1 || j === -1) {
            return null;
        }
        let start = this.corners[Math.max(i, j)].location.clone();
        let end = this.corners[Math.min(i, j)].location.clone();
        if ((i === 0 && j === this.corners.length - 1) || (j === 0 && i === this.corners.length - 1)) {
            end = this.corners[this.corners.length - 1].location.clone();
            start = this.corners[0].location.clone();
        }
        return { 'start': start, 'end': end };
    }

    getWallDirection(wall) {
        let orderedCorners = this.__getOrderedCorners(wall);
        if (orderedCorners === null) {
            return null;
        }
        let start = orderedCorners['start'];
        let end = orderedCorners['end'];
        let vect = end.sub(start);
        let vect3 = new Vector3(vect.x, vect.y, 0);
        return vect3.normalize().clone();
    }

    getWallOutDirection(wall) {
        let vect3 = this.getWallDirection(wall); 
        if (vect3 === null) {
            return null;
        }
        vect3 = vect3.applyAxisAngle(new Vector3(0, 0, 1), Math.PI * 0.5);
        return vect3.normalize();
    }

    getWallStart(wall) {
        let orderedCorners = this.__getOrderedCorners(wall);
        if (orderedCorners === null) {
            return null;
        }
        return orderedCorners['start'];
    }

    getWallEnd(wall) {
        let orderedCorners = this.__getOrderedCorners(wall);
        if (orderedCorners === null) {
            return null;
        }
        return orderedCorners['end'];
    }

    

    roomIdentifier() {
        let cornerids = [];
        this.corners.forEach((corner) => {
            cornerids.push(corner.id);
        });
        let ids = cornerids.join(',');
        return ids;
    }

    getUuid() {
        let cornerUuids = Utils.map(this.corners, function (c) { return c.id; });
        cornerUuids.sort();
        return cornerUuids.join();
    }

    fireOnFloorChange(callback) {
        this.floorChangeCallbacks.add(callback);
    }

    setRoomWallsTexture(textureUrl, textureStretch, textureScale) {
        let edge = this.edgePointer;
        let iterateWhile = true;
        edge.setTexture(textureUrl, textureStretch, textureScale);
        while (iterateWhile) {
            if (edge.next === this.edgePointer) {
                break;
            } else {
                edge = edge.next;
            }
            edge.setTexture(textureUrl, textureStretch, textureScale);
        }
    }

    

    
    getRoomCornerId(){
        return this.__cornerids;
    }
    

    cycleIndex(index) {
        if (index < 0) {
            return index += this.corners.length;
        } else {
            return index % this.corners.length;
        }
    }

    pointInRoom(pt) {
        let polygon = [];
        this.corners.forEach((corner) => {
            let co = new Vector2(corner.x, corner.y);
            polygon.push(co);
        });
        return Utils.pointInPolygon2(pt, polygon);
    }

    updateInteriorCorners() {
        let minB = new Vector2(Number.MAX_VALUE, Number.MAX_VALUE);
        let maxB = new Vector2(-Number.MAX_VALUE, -Number.MAX_VALUE);
        let edge = this.edgePointer;
        let iterateWhile = true;
        this.interiorCorners = [];
        this.interiorCorners3D = [];
        while (iterateWhile) {
            let iStart = edge.interiorStart();
            let cStart = edge.getStart();
            minB.x = Math.min(iStart.x, minB.x);
            minB.y = Math.min(iStart.y, minB.y);
            maxB.x = Math.max(maxB.x, iStart.x);
            maxB.y = Math.max(maxB.y, iStart.y);
            this.interiorCorners.push(iStart);
            this.interiorCorners3D.push(new Vector3(iStart.x, cStart.elevation, iStart.y));
            edge.generatePlane();
            if (edge.next === this.edgePointer) {
                break;
            } else {
                edge = edge.next;
            }
        }
        this.floorRectangleSize = maxB.clone().sub(minB);
    }

    updateArea() {
        let oldarea = this.area;
        let points = [];
        let allpoints = [];
        this.areaCenter = new Vector2();
        this._polygonPoints = [];

        let firstCorner, secondCorner, wall, i, corner, region;

        for (i = 0; i < this.corners.length; i++) {
            corner = this.corners[i];
            firstCorner = this.corners[i];
            secondCorner = this.corners[(i + 1) % this.corners.length];
            wall = firstCorner.wallToOrFrom(secondCorner);

            if (wall != null) {
                if (wall.wallType === WallTypes.CURVED) {
                    let begin = corner.location.clone().sub(wall.bezier.get(0)).length();
                    let p;
                    let stepIndex;
                    allpoints.push(corner.location.clone());

                    if (begin < 1e-6) {
                        for (stepIndex = 1; stepIndex < 20; stepIndex++) {
                            p = wall.bezier.get(stepIndex / 20);
                            allpoints.push(new Vector2(p.x, p.y));
                        }
                    } else {
                        for (stepIndex = 19; stepIndex > 0; stepIndex--) {
                            p = wall.bezier.get(stepIndex / 20);
                            allpoints.push(new Vector2(p.x, p.y));
                        }
                    }
                } else {
                    allpoints.push(corner.location.clone());
                }
            } else {
                allpoints.push(corner.location.clone());
            }
        }

        points = allpoints;
        region = new Region(points);
        this.area = Math.abs(region.area());
        this.areaCenter = region.centroid();
        this._polygonPoints = points;

       

        this.dispatchEvent({ type: EVENT_ROOM_ATTRIBUTES_CHANGED, item: this, info: { from: oldarea, to: this.area } });
    }

    updateArea2() {
        let scope = this;
        let isComplexRoom = false;
        let oldarea = this.area;
        let points = [];
        let N = 0;
        let area = 0;
        this.areaCenter = new Vector2();
        this._polygonPoints = [];

       


        for (let i = 0; i < this.corners.length; i++) {
            let firstCorner = this.corners[i];
            let secondCorner = this.corners[(i + 1) % this.corners.length];
            let wall = firstCorner.wallToOrFrom(secondCorner);
            isComplexRoom |= (wall.wallType === WallTypes.CURVED);
        }

        let inext, a, b, ax_by, ay_bx, delta;
        if (!isComplexRoom) {
            this.corners.forEach((corner) => {
                let co = new Vector2(corner.x, corner.y);
                scope.areaCenter.add(co);
                points.push(co);
            });
            this.areaCenter.multiplyScalar(1.0 / points.length);
            for (let i = 0; i < points.length; i++) {
                inext = (i + 1) % points.length;
                a = points[i];
                b = points[inext];
                ax_by = (a.x * b.y);
                ay_bx = (a.y * b.x);
                delta = ax_by - ay_bx;
                area += delta;
            }
            this.area = Math.abs(area) * 0.5;
            this._polygonPoints = points;
            this.dispatchEvent({ type: EVENT_ROOM_ATTRIBUTES_CHANGED, item: this, info: { from: oldarea, to: this.area } });
            return;
        }


        //		this.corners.forEach((corner) => {
        //			var co = new Vector2(corner.x,corner.y);
        //			this.areaCenter.add(co);
        //			points.push(co);
        //		});

        N = this.corners.length;

        for (let i = 0; i < this.corners.length; i++) {
            let firstCorner = this.corners[i];
            let secondCorner = this.corners[(i + 1) % this.corners.length];
            let wall = firstCorner.wallToOrFrom(secondCorner);
            this.areaCenter.add(firstCorner.location);

            if (wall != null) {
                if (wall.wallType === WallTypes.CURVED) {
                    points.push(firstCorner.location);
                    let LUT = wall.bezier.getLUT(20);
                    for (let j = 1; j < LUT.length - 1; j++) {
                        let p = LUT[j];
                        p = new Vector2(p.x, p.y);
                        points.push(p);
                    }
                } else {
                    points.push(firstCorner.location);
                }
            } else {
                points.push(firstCorner.location);
            }
        }

        this.areaCenter.multiplyScalar(1.0 / N);

        let indicesAndAngles = Utils.getCyclicOrder(points, this.areaCenter);
        points = indicesAndAngles['points'];

        for (let i = 0; i < points.length; i++) {
            inext = (i + 1) % points.length;
            a = points[i];
            b = points[inext];
            //Another irregular polygon method based on the url below
            //https://www.mathsisfun.com/geometry/area-irregular-polygons.html
            //			var width = a.x - b.x;
            //			var height = (a.y + b.y) * 0.5;
            //			var delta = Math.abs(width * height);
            ax_by = (a.x * b.y);
            ay_bx = (a.y * b.x);
            delta = ax_by - ay_bx;
            area += delta;
        }
        this._polygonPoints = points;
        this.area = Math.abs(area) * 0.5;
        //		if we are using the method in url https://www.mathsisfun.com/geometry/area-irregular-polygons.html 
        //		then we dont have to multiply the area by 0.5;
        //		this.area = Math.abs(area);
        this.dispatchEvent({ type: EVENT_ROOM_ATTRIBUTES_CHANGED, item: this, info: { from: oldarea, to: this.area } });
    }

    hasAllCornersById(ids) {
        let sum = 0;
        for (let i = 0; i < ids.length; i++) {
            sum += this.hasACornerById(ids[i]);
        }
        return (sum === this.corners.length);
    }

    hasACornerById(id) {
        for (let i = 0; i < this.corners.length; i++) {
            let corner = this.corners[i];
            if (corner.id === id) {
                return 1;
            }
        }
        return 0;
    }

    /**
     * Populates each wall's half edge relating to this room
     * this creates a fancy doubly connected edge list (DCEL)
     */
    updateWalls() {

        let prevEdge = null;
        let firstEdge = null;
        this.__walls = [];

        for (let i = 0; i < this.corners.length; i++) {

            let firstCorner = this.corners[i];
            let secondCorner = this.corners[(i + 1) % this.corners.length];

            // find if wall is heading in that direction
            let wallTo = firstCorner.wallTo(secondCorner);
            let wallFrom = firstCorner.wallFrom(secondCorner);
            let edge = null;
            if (wallTo) {
                edge = new HalfEdge(this, wallTo, true);
            } else if (wallFrom) {
                edge = new HalfEdge(this, wallFrom, false);
            } else {
                // something horrible has happened
                // console.log('corners arent connected by a wall, uh oh');
            }

            /**
             * Ensure the room contains the list of wall pointers
             */
            if (wallTo && !this.__walls.includes(wallTo)) {
                this.__walls.push(wallTo);
                wallTo.addRoom(this);
            }
            if (wallFrom && !this.__walls.includes(wallFrom)) {
                this.__walls.push(wallFrom);
                wallFrom.addRoom(this);
            }

            if (i === 0) {
                firstEdge = edge;
            } else {
                edge.prev = prevEdge;
                prevEdge.next = edge;
                if (i + 1 === this.corners.length) {
                    firstEdge.prev = edge;
                    edge.next = firstEdge;
                }
            }
            prevEdge = edge;
        }

        // hold on to an edge reference
        this.edgePointer = firstEdge;
    }

    set isLocked(flag) {
        this.__isLocked = flag;
    }

    get isLocked() {
        return this.__isLocked;
    }

    get isDelete(){
        return this.__isDelete;
    }

    set isDelete(value){
        this.__isDelete = value;
        this.dispatchEvent({ type: EVENT_UPDATED, item: this });
        
        // this.dispatchEvent({ type: EVENT_UPDATED, item: this });
    }

    get uuid() {
        return this.getUuid();
    }

    get corners() {
        return this._corners;
    }

    get sharedWalls() {
        return this.__walls;
    }

    get roomCornerPoints() {
        return this._polygonPoints;
    }

    get roomByCornersId() {
        return this._roomByCornersId;
    }

    set name(value) {
        let oldname = this._name;
        this._name = value;
        this.floorplan.dispatchEvent({ type: EVENT_ROOM_ATTRIBUTES_CHANGED, item: this, info: { from: oldname, to: this._name } });
    }
    get name() {
        return this._name;
    }

    get areaCenter_round() {
        return new Vector2(Math.round(this.areaCenter.x),Math.round(this.areaCenter.y));
    }

    set code(value) {
        this._code = value;
        // this.floorplan.dispatchEvent({ type: EVENT_ROOM_ATTRIBUTES_CHANGED, item: this, info: { from: oldname, to: this._name } });
    }
    get code() {
        return this._code;
    }

    set factor(value){
        let oldFactor = this._factor;
        this._factor = value;
        this.floorplan.dispatchEvent({ type: EVENT_ROOM_ATTRIBUTES_CHANGED, item: this, info: { from: oldFactor, to: this._factor } });
    }

    get factor(){
        return this._factor;
    }

    set baseColor(value){
        this._roomBaseColor = value;
    }

    get baseColor(){
        return this._roomBaseColor;
    }

    set roomType(value){
        this._roomType = value;
    }

    get roomType(){
        return this._roomType;
    }


}
export default Room;